import axios from '@/api/provider';
import { handleError } from '@/api/response';

const createSession = async (options = {}) => {
  const notificationId = typeof options === 'object' ? options.notificationId : null;
  const title = typeof options === 'object' ? options.title : null;
  // One impact_v5 LLM call — { run_id, prompt_name, at }. The backend seeds
  // the session's system prompt from it and reuses an existing chat for the
  // same call rather than opening a parallel one.
  const stageCall = typeof options === 'object' ? options.stageCall : null;

  const body = {};
  if (notificationId !== null) {
    body.notification_id = notificationId;
  }
  if (title !== null) {
    body.title = title;
  }
  if (stageCall) {
    body.stage_call = stageCall;
  }

  return axios
    .post('sessions', body)
    .then((response) => response)
    .catch((error) => {
      console.error('Session creation error:', error);
      throw error;
    });
};

const loadSessions = async () => {
  return axios
    .get('sessions')
    .then((response) => response)
    .catch((error) => {
      console.error('Sessions loading error:', error);
      throw error;
    });
};

const deleteSession = async (sessionId) => {
  return axios
    .del('sessions', sessionId)
    .then((response) => response)
    .catch((error) => {
      console.error('Session deletion error:', error);
      throw error;
    });
};

const loadSession = async (sessionId) => {
  return axios
    .get(`sessions/${sessionId}`)
    .then((response) => response)
    .catch((error) => {
      console.error('Session loading error:', error);
      throw error;
    });
};

// Submit a message to a session, returns job_id for polling
const sendMessage = async (sessionId, content, messageType = 'text', audioData = null) => {
  const body = {
    content,
    message_type: messageType
  };

  if (audioData) {
    body.audio_data = audioData;
  }

  return axios
    .post(`sessions/${sessionId}/messages`, body)
    .then((response) => response)
    .catch((error) => {
      console.error('Message send error:', error);
      throw error;
    });
};

// Poll for job status and partial results
const pollJob = async (jobId) => {
  return axios
    .get(`chat/jobs/${jobId}`)
    .then((response) => response)
    .catch((error) => {
      console.error('Job polling error:', error);
      throw error;
    });
};

// Respond to a plan confirmation prompt
const respondToPlan = async (jobId, planId, action) => {
  return axios
    .post(`chat/jobs/${jobId}/plan-response`, { plan_id: planId, action })
    .then((response) => response)
    .catch((error) => {
      console.error('Plan response error:', error);
      throw error;
    });
};

// Cancel an in-progress job
const cancelJob = async (jobId) => {
  return axios
    .post(`chat/jobs/${jobId}/cancel`)
    .then((response) => response)
    .catch((error) => {
      console.error('Job cancel error:', error);
      throw error;
    });
};

// Clean up completed job
const cleanupJob = async (jobId) => {
  return axios
    .del('chat/jobs', jobId)
    .then((response) => response)
    .catch((error) => {
      console.error('Job cleanup error:', error);
      throw error;
    });
};

// ── Chat attachments (files in) + artifacts (documents out) ──────────────

// Attach a file to a session. Extraction is synchronous server-side and can
// be slow for large scanned PDFs (Mistral OCR fallback) — generous timeout.
const uploadAttachment = async (sessionId, file) => {
  const form = new FormData();
  form.append('file', file);

  return axios
    .post(`sessions/${sessionId}/attachments`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 5 * 60 * 1000
    })
    .catch((error) => {
      console.error('Attachment upload error:', error);
      throw error;
    });
};

const listAttachments = async (sessionId) => {
  return axios
    .get(`sessions/${sessionId}/attachments`)
    .catch((error) => {
      console.error('Attachment list error:', error);
      throw error;
    });
};

const deleteAttachment = async (sessionId, attachmentId) => {
  return axios
    .delete(`sessions/${sessionId}/attachments/${attachmentId}`)
    .catch((error) => {
      console.error('Attachment delete error:', error);
      throw error;
    });
};

// Download a generated document (create_document tool output) as a Blob.
// Bypasses the default get() wrapper so the binary body survives.
const downloadArtifact = async (artifactId) => {
  const instance = axios.getAxiosInstance();
  const config = axios.getConfig();
  try {
    const response = await instance.get(`chat/artifacts/${artifactId}`, {
      ...config,
      responseType: 'blob'
    });
    return response.data;
  } catch (e) {
    // bypassing the get() wrapper skips handleError's 401 redirect — restore it
    return handleError(e);
  }
};

// Re-run the session's last question when it never got an answer. The backend
// reads the question from the saved chat (it is not saved twice) and returns a
// job_id to poll, like sendMessage. 409 when the last question already has an answer.
const retryLastMessage = async (sessionId) => {
  return axios
    .post(`sessions/${sessionId}/retry`)
    .then((response) => response)
    .catch((error) => {
      console.error('Retry error:', error);
      throw error;
    });
};

export default {
  createSession,
  retryLastMessage,
  loadSessions,
  deleteSession,
  loadSession,
  sendMessage,
  pollJob,
  respondToPlan,
  cancelJob,
  cleanupJob,
  uploadAttachment,
  listAttachments,
  deleteAttachment,
  downloadArtifact
};
